// useTopLevelNodeCreate.js

import { useCallback, useRef, useState } from 'react';

import { toast } from 'react-toastify';

// Utilities
import { SectionType } from 'utilities/Enums';
import { buildNodePath } from 'utilities/Routing';
import { createNode as createNodeFromType } from 'utilities/NodeFactory';
import { getPluralLabel, getSingularLabel } from 'utilities/Languages';
import { buildFileMetadata } from 'editor/nodeadd/utilities/NodeAddUtilities';
import { getResolvedFields } from 'utilities/NodeType';

// Services
import { ensureTypeDatabaseScaffold } from 'services/nodetypes/NodeTypeWriteService';
import { upsertEdge } from 'services/nodes/NodeEdgeService';
import { createNode as createNodeDoc } from 'services/nodes/NodeWriteService';
import { uploadService } from 'services/nodes/NodeUploadService';
import { assertWorkspaceUploadAllowed } from 'services/workspaces/WorkspaceUploadPolicyService';

const DEFAULT_UPLOAD_ACCEPT = {
    PHOTO: 'image/*',
    VIDEO: 'video/*',
    DOCUMENT: 'application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword,text/plain,application/rtf,application/vnd.oasis.opendocument.text',
    FILE: 'image/*,video/*,application/*,text/*',
};

const ALWAYS_MULTI_UPLOAD_PRIMITIVES = new Set(['PHOTO', 'VIDEO', 'DOCUMENT', 'FILE']);

const buildDefaultDatabaseKey = (workspaceKey, typeKey) => {
    if (!workspaceKey || !typeKey) return '';
    return `${workspaceKey}_${typeKey}_all`.replace(/\//g, '_');
};

export default function useTopLevelNodeCreate({
    user,
    activeWorkspaceKey,
    getType,
    language,
    navigate,
    setCurrentNode,
    setCurrentSection,
    setSelectedLocation,
    setActiveDatabase,
    shouldNavigate = true,
    onNodesCreated,
}) {
    const uploadInputRef = useRef(null);
    const [pendingUploadType, setPendingUploadType] = useState(null);
    const [pendingUploadCreateType, setPendingUploadCreateType] = useState(null);
    const [pendingUploadAccept, setPendingUploadAccept] = useState('');
    const [pendingUploadFields, setPendingUploadFields] = useState(null);
    const [pendingUploadNavigate, setPendingUploadNavigate] = useState(null);
    const [pendingUploadMultiple, setPendingUploadMultiple] = useState(false);
    const [cameraOpen, setCameraOpen] = useState(false);
    const [cameraContentType, setCameraContentType] = useState(null);
    const [cameraInitialFields, setCameraInitialFields] = useState(null);
    const [cameraShouldNavigate, setCameraShouldNavigate] = useState(null);

    const notifyNodesCreated = useCallback((createdNodes) => {
        if (typeof onNodesCreated !== 'function') return;

        const rows = (Array.isArray(createdNodes) ? createdNodes : [])
            .filter((row) => row?.key);

        if (!rows.length) return;

        try {
            onNodesCreated(rows);
        } catch (error) {
            console.warn('onNodesCreated callback failed:', error);
        }
    }, [onNodesCreated]);

    const assertFilesAllowedForWorkspace = useCallback(async (files) => {
        const safeFiles = Array.isArray(files) ? files.filter(Boolean) : [];
        if (!safeFiles.length) return;

        await Promise.all(safeFiles.map((file) => {
            return assertWorkspaceUploadAllowed({
                workspaceKey: activeWorkspaceKey,
                fileName: file?.name,
                fileSizeBytes: file?.size,
            });
        }));
    }, [activeWorkspaceKey]);

    const resolveSectionKeyForField = useCallback((typeKey, fieldKey) => {
        const def = getType?.(typeKey);
        const fieldName = typeof fieldKey === 'string' ? fieldKey.trim() : '';

        if (!def || !fieldName) return def?.dataSection || null;

        const schemaFields = Array.isArray(def?.schema?.fields) ? def.schema.fields : [];
        const resolvedFields = getResolvedFields(typeKey, { includeHidden: true });
        const allFields = [
            ...schemaFields,
            ...(Array.isArray(resolvedFields) ? resolvedFields : []),
        ];

        const fieldDef = allFields.find((field) => {
            const candidateName = field?.name || field?.key;
            return candidateName === fieldName;
        }) || null;

        const sourceTypeKey = String(fieldDef?.source || def?.key || typeKey || '').trim();
        if (!sourceTypeKey) return def?.dataSection || null;

        const sourceDef = getType?.(sourceTypeKey);
        if (sourceDef?.dataSection) return sourceDef.dataSection;

        const normalizedSource = sourceTypeKey.toLowerCase();
        return normalizedSource ? `data_${normalizedSource}` : (def?.dataSection || null);
    }, [getType]);

    const applyInitialFields = useCallback((targetNode, typeKey, initialFields) => {
        if (!targetNode || !initialFields) return;
        const def = getType?.(typeKey);
        if (!def) return;

        const containersBySection = new Map();

        Object.entries(initialFields).forEach(([fieldKey, value]) => {
            if (value === undefined) return;

            const sectionKey = resolveSectionKeyForField(typeKey, fieldKey);
            if (!sectionKey) return;

            const existing = containersBySection.get(sectionKey)
                || ((targetNode[sectionKey] && typeof targetNode[sectionKey] === 'object')
                    ? { ...targetNode[sectionKey] }
                    : {});

            existing[fieldKey] = value;
            containersBySection.set(sectionKey, existing);
        });

        containersBySection.forEach((container, sectionKey) => {
            if (Object.keys(container).length > 0) {
                targetNode[sectionKey] = container;
            }
        });
    }, [getType, resolveSectionKeyForField]);

    const createNodeForFile = useCallback(async ({ file, createTypeKey, initialFields }) => {
        const newNode = await createNodeFromType(createTypeKey, user, activeWorkspaceKey || null);

        const typeDef = getType?.(newNode.type || createTypeKey);
        const singularLabel = getSingularLabel(typeDef, newNode.type || createTypeKey, language);
        let defaultTitle = `New ${singularLabel}`;

        newNode.parentTitle = user.name || 'Profile';
        newNode.parentType = user.type || 'USER';
        newNode.profileRoot = true;

        newNode.fileName = file.name;
        const objectUrl = URL.createObjectURL(file);
        newNode.localUri = objectUrl;
        newNode.url = objectUrl;

        const def = getType?.(createTypeKey);
        const primitiveKey = def?.primitiveKey || createTypeKey;
        if (primitiveKey === 'DOCUMENT' || primitiveKey === 'FILE') {
            const metadata = buildFileMetadata(file, primitiveKey);
            if (metadata.data_document) newNode.data_document = metadata.data_document;
            if (metadata.data_file) newNode.data_file = metadata.data_file;
            if (metadata.title) defaultTitle = metadata.title;
        }

        newNode.title = defaultTitle;

        applyInitialFields(newNode, newNode.type || createTypeKey, initialFields);

        const createPayload = { ...newNode };
        delete createPayload.localUri;
        delete createPayload.url;
        delete createPayload.base64Data;
        await createNodeDoc(newNode.key, createPayload);

        return { node: newNode, file, objectUrl };
    }, [activeWorkspaceKey, applyInitialFields, getType, language, user]);

    const processUploadFiles = useCallback(async ({
        files,
        typeKey,
        createTypeKey,
        initialFields,
        isMultiUpload,
        shouldNavigateNow,
    }) => {
        if (!files.length || !typeKey || !user) return null;

        try {
            await assertFilesAllowedForWorkspace(files);

            if (isMultiUpload) {
                if (shouldNavigateNow) {
                    const databaseKey = buildDefaultDatabaseKey(activeWorkspaceKey, createTypeKey);
                    if (databaseKey && navigate) {
                        if (typeof setActiveDatabase === 'function') {
                            const typeDef = getType?.(createTypeKey);
                            const pluralLabel = getPluralLabel(typeDef, createTypeKey, language);
                            setActiveDatabase({
                                key: databaseKey,
                                type: createTypeKey,
                                title: `All ${pluralLabel}`,
                                workspaceKey: activeWorkspaceKey || null,
                                userKey: user?.key || null,
                                filters: [],
                            });
                        }
                        setSelectedLocation && setSelectedLocation(null);
                        setCurrentNode && setCurrentNode(null);
                        setCurrentSection && setCurrentSection(SectionType.NODE);
                        navigate(`/database/${databaseKey}`);
                    }
                }

                const entries = [];

                for (const file of files) {
                    const entry = await createNodeForFile({ file, createTypeKey, initialFields });
                    if (entry) entries.push(entry);
                }

                const cleanup = () => {
                    entries.forEach((entry) => {
                        if (entry?.objectUrl) URL.revokeObjectURL(entry.objectUrl);
                    });
                };

                const handleUploadError = (error) => {
                    cleanup();
                    toast.error(error?.message || 'Upload failed.');
                };

                uploadService.queueNodeUploads(entries, user, cleanup, handleUploadError);
                const createdNodes = entries.map((entry) => entry?.node).filter(Boolean);
                notifyNodesCreated(createdNodes);
                return createdNodes;
            }

            const [file] = files;
            const entry = await createNodeForFile({ file, createTypeKey, initialFields });
            const newNode = entry?.node;

            if (shouldNavigateNow) {
                setSelectedLocation && setSelectedLocation(null);
                setCurrentNode && setCurrentNode({ ...newNode, _updated: Date.now() });
                setCurrentSection && setCurrentSection(SectionType.NODE);
                navigate && navigate(buildNodePath(newNode.key));
            }

            const entries = entry ? [entry] : [];
            const cleanup = () => {
                entries.forEach((row) => {
                    if (row?.objectUrl) URL.revokeObjectURL(row.objectUrl);
                });
            };

            const handleUploadError = (error) => {
                cleanup();
                toast.error(error?.message || 'Upload failed.');
            };

            uploadService.queueNodeUploads(entries, user, cleanup, handleUploadError);
            notifyNodesCreated(newNode ? [newNode] : []);
            return newNode || null;
        } catch (error) {
            console.error('Failed to create upload node:', error);
            toast.error(error?.message || 'Could not prepare upload.');
            return null;
        }
    }, [
        activeWorkspaceKey,
        assertFilesAllowedForWorkspace,
        createNodeForFile,
        getType,
        language,
        navigate,
        setActiveDatabase,
        setCurrentNode,
        setCurrentSection,
        setSelectedLocation,
        notifyNodesCreated,
        user,
    ]);

    const handleTopLevelActionSelect = useCallback(async (typeKey, options = {}) => {
        if (!user || !typeKey) return null;

        const initialFields = options?.initialFields || null;
        const relatedNode = options?.relatedNode || null;
        const shouldNavigateOverride = typeof options?.shouldNavigate === 'boolean'
            ? options.shouldNavigate
            : null;
        const shouldNavigateNow = shouldNavigateOverride ?? shouldNavigate;

        if (typeKey === 'SNAPSHOT' || typeKey === 'RECORDING') {
            setCameraContentType(typeKey);
            setCameraInitialFields(initialFields);
            setCameraShouldNavigate(shouldNavigateNow);
            setCameraOpen(true);
            return null;
        }

        const def = getType?.(typeKey);
        const primitiveKey = def?.primitiveKey || typeKey;
        const primitiveKeyUpper = String(primitiveKey).toUpperCase();
        const acceptOverride = typeof def?.uploadAccept === 'string' ? def.uploadAccept.trim() : '';
        const fallbackAccept = DEFAULT_UPLOAD_ACCEPT[primitiveKeyUpper] || '';
        const isUploadType = Boolean(acceptOverride || fallbackAccept || def?.multiUpload);
        const resolvedAccept = acceptOverride || fallbackAccept || DEFAULT_UPLOAD_ACCEPT.FILE;
        const isMultiUpload = def?.multiUpload === true || ALWAYS_MULTI_UPLOAD_PRIMITIVES.has(primitiveKeyUpper);
        const uploadCreatesType = typeof def?.uploadCreatesType === 'string'
            ? def.uploadCreatesType.trim()
            : '';

        if (isUploadType) {
            setPendingUploadType(typeKey);
            setPendingUploadCreateType(isMultiUpload ? (uploadCreatesType || typeKey) : null);
            setPendingUploadAccept(resolvedAccept);
            setPendingUploadFields(initialFields);
            setPendingUploadNavigate(shouldNavigateNow);
            setPendingUploadMultiple(isMultiUpload);
            requestAnimationFrame(() => uploadInputRef.current?.click());
            return null;
        }

        try {
            const newNode = await createNodeFromType(typeKey, user, activeWorkspaceKey || null);

            const typeDef = getType?.(newNode.type || typeKey);
            const singularLabel = getSingularLabel(typeDef, newNode.type || typeKey, language);
            const resolvedTypeKey = String(newNode.type || typeKey || '').trim();
            newNode.title = `New ${singularLabel}`;
            newNode.parentTitle = user.name || 'Profile';
            newNode.parentType = user.type || 'USER';
            newNode.profileRoot = true;

            applyInitialFields(newNode, newNode.type || typeKey, initialFields);

            if (activeWorkspaceKey && resolvedTypeKey && user?.key) {
                try {
                    await ensureTypeDatabaseScaffold({
                        userKey: user.key,
                        workspaceKey: activeWorkspaceKey,
                        typeKey: resolvedTypeKey,
                        labelPlural: typeDef?.labelPlural || typeDef?.label || resolvedTypeKey,
                        defaultView: typeDef?.defaultView,
                    });
                } catch (error) {
                    console.warn('Failed to ensure type database scaffold:', error);
                }
            }

            await createNodeDoc(newNode.key, newNode);

            if (relatedNode?.key) {
                try {
                    await upsertEdge(relatedNode.key, newNode.key, {
                        workspaceKey: activeWorkspaceKey || null,
                    });
                } catch (error) {
                    console.error('Failed to link created node to related node:', error);
                }
            }

            if (shouldNavigateNow) {
                setSelectedLocation && setSelectedLocation(null);
                setCurrentNode && setCurrentNode({ ...newNode, _updated: Date.now() });
                setCurrentSection && setCurrentSection(SectionType.NODE);
                navigate && navigate(buildNodePath(newNode.key));
            }

            notifyNodesCreated([newNode]);

            return newNode;
        } catch (error) {
            console.error('Failed to create node:', error);
            toast.error(error?.message || 'Could not create node.');
            return null;
        }
    }, [
        user,
        activeWorkspaceKey,
        getType,
        language,
        navigate,
        setCurrentNode,
        setCurrentSection,
        setSelectedLocation,
        applyInitialFields,
        notifyNodesCreated,
        shouldNavigate,
        setCameraContentType,
        setCameraInitialFields,
        setCameraShouldNavigate,
        setCameraOpen,
    ]);

    const handleUploadFileSelect = useCallback(async (event) => {
        const files = Array.from(event?.target?.files || []);
        if (event?.target) event.target.value = '';

        const typeKey = pendingUploadType;
        const createTypeKey = pendingUploadCreateType || typeKey;
        const initialFields = pendingUploadFields;
        const isMultiUpload = pendingUploadMultiple;
        setPendingUploadType(null);
        setPendingUploadCreateType(null);
        setPendingUploadAccept('');
        setPendingUploadFields(null);
        setPendingUploadMultiple(false);
        const shouldNavigateNow = pendingUploadNavigate ?? shouldNavigate;
        setPendingUploadNavigate(null);
        await processUploadFiles({
            files,
            typeKey,
            createTypeKey,
            initialFields,
            isMultiUpload,
            shouldNavigateNow,
        });
    }, [
        pendingUploadType,
        pendingUploadCreateType,
        pendingUploadFields,
        pendingUploadNavigate,
        pendingUploadMultiple,
        processUploadFiles,
        shouldNavigate,
    ]);

    const handleCameraContentCreated = useCallback(async (capturedContent) => {
        const typeFromIntent = cameraContentType === 'RECORDING' ? 'VIDEO' : 'PHOTO';
        const mimeType = capturedContent?.mimeType || (typeFromIntent === 'VIDEO' ? 'video/webm' : 'image/jpeg');
        const fallbackName = capturedContent?.fileName
            || `${typeFromIntent.toLowerCase()}-${Date.now()}${typeFromIntent === 'VIDEO' ? '.webm' : '.jpg'}`;

        let file = capturedContent?.tempFile || capturedContent?.localFile || null;
        if (file instanceof Blob && !(file instanceof File)) {
            file = new File([file], fallbackName, { type: file.type || mimeType });
        }

        setCameraOpen(false);
        setCameraContentType(null);

        const initialFields = cameraInitialFields;
        const shouldNavigateNow = cameraShouldNavigate ?? shouldNavigate;
        setCameraInitialFields(null);
        setCameraShouldNavigate(null);

        if (!file) {
            console.warn('Camera capture did not produce an uploadable file.');
            return;
        }

        await processUploadFiles({
            files: [file],
            typeKey: typeFromIntent,
            createTypeKey: typeFromIntent,
            initialFields,
            isMultiUpload: false,
            shouldNavigateNow,
        });
    }, [cameraContentType, cameraInitialFields, cameraShouldNavigate, processUploadFiles, shouldNavigate]);

    return {
        cameraOpen,
        cameraContentType,
        uploadInputRef,
        pendingUploadAccept,
        pendingUploadMultiple,
        handleTopLevelActionSelect,
        handleUploadFileSelect,
        setCameraOpen,
        handleCameraContentCreated,
    };
}
